import { FormCard } from '@qbe/ads-blocks-forms';
import { Button, Spinner } from '@qbe/ads-core';
import { useMFEContainer } from '@qbe/dfc-mfe-components';
import { MFEEventPayload } from '@qbe/dfc-micro-frontends';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { config } from '~/config';
import { QUOTE_ROUTE } from '~/constants/routes';
import { useAppContent, useAppDispatch, useAppNavigate, useExtraInfo } from '~/hooks';
import { updatePartnerMembershipNumber } from '~/slices/extraInfo';
import { navigateAction } from '~/thunks/navigateAction';

const validationRules = {
  memberVerifyNumber: {
    pattern: '^\\d*$',
    maxLength: 11,
  },
};

const IFRAME_CONTAINER_ID = 'mfeIframeContainer';
const mfeName = 'mvmfe';

const resizeIframeContainer = (height: number) => {
  const iframeContainer = document.getElementById('iframeApp-mvmfe');
  if (iframeContainer) {
    iframeContainer.style.height = `${height}px`;
  }
};

const Membership: FC = () => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const memberNumberRef = useRef<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { getTextContent } = useAppContent({
    basePath: 'dsme.membership',
  });

  const { partnerMembershipNumber } = useExtraInfo();
  const [isSeeQuoteEnabled, setIsSeeQuoteEnabled] = useState<boolean>(
    Boolean(partnerMembershipNumber)
  );

  const handlePostMessage = useCallback(
    (event: { data: { height: number }; origin: string }) => {
      const velocityOrigin = new URL(config.velocityUrl as string).origin;
      // Validate the origin of the message
      if (event.origin !== velocityOrigin) {
        return; // Ignore messages from untrusted origins
      }

      if (event.data?.height) {
        resizeIframeContainer(
          event.data.height + Number(getTextContent('content.formContainerFallbackHeight.title'))
        );
      }
    },
    [getTextContent]
  );

  useEffect(() => {
    window.addEventListener('message', handlePostMessage);
    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('message', handlePostMessage);
    };
  }, [handlePostMessage]);

  const onSubmit = () => {
    dispatch(
      navigateAction({
        to: QUOTE_ROUTE,
        navigate,
      })
    );
  };

  const onReceiveMessage = (event: MFEEventPayload) => {
    if (event.type === 'message') {
      if (
        event.isValid &&
        typeof event.message === 'object' &&
        event.message !== null &&
        typeof (event.message as Record<string, unknown>).memberVerifyNumber === 'string'
      ) {
        memberNumberRef.current = (
          event.message as { memberVerifyNumber: string }
        ).memberVerifyNumber;
        dispatch(updatePartnerMembershipNumber(memberNumberRef.current));
        setIsSeeQuoteEnabled(true);
      }
    } else if (event.type === 'submitting') {
      setIsLoading(Boolean(event.isSubmitting));
    } else if (event.type === 'reset') {
      setIsSeeQuoteEnabled(false);
      dispatch(updatePartnerMembershipNumber(''));
    }
  };

  useMFEContainer({
    name: mfeName,
    url: config.velocityUrl as string,
    selector: `#${IFRAME_CONTAINER_ID}`,
    contextData: {
      affinityGroup: 'velocity',
      appType: 'vabf',
      data: {
        memberVerifyNumber: partnerMembershipNumber,
        memberVerifyFirstName: 'Dummy',
        memberVerifyLastName: 'Membership',
      },
      validationRules,
      parentOrigin: window.location.origin,
    },
    onMessage: onReceiveMessage,
    iframeAttributes: {
      width: '100%',
      style: {
        overflow: 'hidden',
        border: 'none',
      },
    },
  });
  return (
    <div>
      <Spinner loading={isLoading} description={getTextContent('content.loading.title')} />
      <FormCard
        data-testid="business-details"
        title={getTextContent('title')}
        variant="pageTitle"
        description={getTextContent('description')}
      />
      <br />
      <FormCard variant="content">
        <div id="mfeIframeContainer" style={{ width: '100%' }} />
        <small>{getTextContent('caption')}</small>
      </FormCard>
      <br />

      <FormCard variant="content">
        <Button
          data-track="velocity membership"
          type="submit"
          onClick={onSubmit}
          label={getTextContent('buttons.seeQuote')}
          disabled={!isSeeQuoteEnabled}
        />
      </FormCard>
    </div>
  );
};

export default Membership;
